/**
 * Customer Portal New Ticket Page
 * Create a new support ticket
 */

import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import CustomerPortalLayout from '@/components/customer-portal/CustomerPortalLayout';
import { customerPortalService, type KBArticle } from '@/services/api/customerPortal';
import {
  ArrowLeft,
  Send,
  AlertCircle,
  Lightbulb,
  BookOpen,
  ArrowRight,
} from 'lucide-react';

export default function CustomerPortalNewTicketPage() {
  const navigate = useNavigate();
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('medium');
  const [categoryId, setCategoryId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [suggestedArticles, setSuggestedArticles] = useState<KBArticle[]>([]);
  const [searchingArticles, setSearchingArticles] = useState(false);

  // Debounced search for relevant KB articles
  useEffect(() => {
    const searchTimer = setTimeout(async () => {
      if (subject.length > 10) {
        setSearchingArticles(true);
        try {
          const result = await customerPortalService.searchKB(subject);
          setSuggestedArticles(result.results.slice(0, 3));
        } catch (err) {
          console.error('Failed to search KB:', err);
        } finally {
          setSearchingArticles(false);
        }
      } else {
        setSuggestedArticles([]);
      }
    }, 500);

    return () => clearTimeout(searchTimer);
  }, [subject]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!subject.trim() || !description.trim()) {
      setError('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const result = await customerPortalService.createTicket({
        subject: subject.trim(),
        description: description.trim(),
        priority,
        categoryId: categoryId || undefined,
      });

      // Redirect to the new ticket
      navigate(`/customer-portal/tickets/${result.id}`);
    } catch (err: any) {
      setError(err.message || 'Failed to create ticket. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <CustomerPortalLayout title="Submit a Ticket">
      <div className="max-w-3xl mx-auto">
        {/* Back Link */}
        <Link
          to="/customer-portal/tickets"
          className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back to Tickets
        </Link>

        <Card>
          <CardHeader>
            <CardTitle>Create Support Ticket</CardTitle>
            <CardDescription>
              Fill out the form below to submit a new support request. Our team will respond as soon as possible.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {/* Subject */}
              <div className="space-y-2">
                <Label htmlFor="subject">
                  Subject <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="subject"
                  placeholder="Brief summary of your issue"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  required
                  maxLength={200}
                />
                <p className="text-xs text-gray-500">
                  {subject.length}/200 characters
                </p>
              </div>

              {/* Suggested Articles */}
              {(suggestedArticles.length > 0 || searchingArticles) && (
                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                  <div className="flex items-center space-x-2 mb-3">
                    <Lightbulb className="w-5 h-5 text-blue-600" />
                    <h4 className="font-medium text-blue-900 dark:text-blue-100">
                      Before you submit, check these articles:
                    </h4>
                  </div>
                  {searchingArticles ? (
                    <div className="text-sm text-blue-700 dark:text-blue-300">
                      Searching knowledge base...
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {suggestedArticles.map((article) => (
                        <Link
                          key={article.id}
                          to={`/customer-portal/knowledge-base/${article.slug}`}
                          target="_blank"
                          className="flex items-center justify-between p-2 rounded bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors group"
                        >
                          <div className="flex items-center space-x-2">
                            <BookOpen className="w-4 h-4 text-blue-500" />
                            <span className="text-sm text-gray-900 dark:text-white group-hover:text-blue-600">
                              {article.title}
                            </span>
                          </div>
                          <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-blue-600" />
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Description */}
              <div className="space-y-2">
                <Label htmlFor="description">
                  Description <span className="text-red-500">*</span>
                </Label>
                <Textarea
                  id="description"
                  placeholder="Please describe your issue in detail. Include any relevant information such as error messages, steps to reproduce, etc."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  required
                  rows={6}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Priority */}
                <div className="space-y-2">
                  <Label htmlFor="priority">Priority</Label>
                  <Select value={priority} onValueChange={setPriority}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select priority" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">
                        <div className="flex items-center">
                          <span className="w-2 h-2 rounded-full bg-green-500 mr-2" />
                          Low - General question
                        </div>
                      </SelectItem>
                      <SelectItem value="medium">
                        <div className="flex items-center">
                          <span className="w-2 h-2 rounded-full bg-amber-500 mr-2" />
                          Medium - Standard issue
                        </div>
                      </SelectItem>
                      <SelectItem value="high">
                        <div className="flex items-center">
                          <span className="w-2 h-2 rounded-full bg-red-500 mr-2" />
                          High - Urgent problem
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Category (optional) */}
                <div className="space-y-2">
                  <Label htmlFor="category">Category (optional)</Label>
                  <Select value={categoryId} onValueChange={setCategoryId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">No category</SelectItem>
                      <SelectItem value="billing">Billing & Payments</SelectItem>
                      <SelectItem value="technical">Technical Issue</SelectItem>
                      <SelectItem value="account">Account & Access</SelectItem>
                      <SelectItem value="feature">Feature Request</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Submit Button */}
              <div className="flex items-center justify-end space-x-3 pt-4 border-t">
                <Link to="/customer-portal/tickets">
                  <Button type="button" variant="outline">
                    Cancel
                  </Button>
                </Link>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="bg-v2accent hover:bg-v2accent/90"
                >
                  {submitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Submit Ticket
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Help Tips */}
        <Card className="mt-6 bg-gray-50 dark:bg-gray-800/50">
          <CardContent className="pt-6">
            <h4 className="font-medium text-gray-900 dark:text-white mb-3">
              Tips for faster resolution:
            </h4>
            <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
              <li className="flex items-start">
                <span className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-2 mr-2 flex-shrink-0" />
                Be specific about the issue - include error messages if any
              </li>
              <li className="flex items-start">
                <span className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-2 mr-2 flex-shrink-0" />
                Describe the steps to reproduce the problem
              </li>
              <li className="flex items-start">
                <span className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-2 mr-2 flex-shrink-0" />
                Mention what you've already tried
              </li>
              <li className="flex items-start">
                <span className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-2 mr-2 flex-shrink-0" />
                Include your browser and device information if relevant
              </li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </CustomerPortalLayout>
  );
}
